import { useLanguage } from '../context/LanguageContext';
import { ArrowDown, Award } from 'lucide-react';

const HeroSection = ({ onBookCall }) => {
  const { t, language } = useLanguage();

  const scrollToAbout = () => {
    const element = document.querySelector('#about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="hero" 
      className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20"
      style={{ 
        background: 'linear-gradient(180deg, var(--warm-white) 0%, var(--sand-50) 50%, var(--sage-50) 100%)'
      }}
      data-testid="hero-section"
    >
      {/* Decorative elements */}
      <div 
        className="absolute top-20 right-10 w-64 h-64 rounded-full opacity-20 blur-3xl"
        style={{ backgroundColor: 'var(--sage-300)' }}
      />
      <div 
        className="absolute bottom-20 left-10 w-80 h-80 rounded-full opacity-20 blur-3xl"
        style={{ backgroundColor: 'var(--sand-300)' }}
      />

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-16 md:py-24 text-center relative z-10">
        {/* Trust badge */}
        <div 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 animate-fade-in"
          style={{ backgroundColor: 'var(--sage-100)', border: '1px solid var(--sage-200)' }}
          data-testid="hero-trust-badge"
        >
          <Award size={16} style={{ color: 'var(--sage-600)' }} />
          <span className="text-sm font-medium" style={{ color: 'var(--sage-700)' }}>
            {language === 'he' ? 'מאמן בשיטת סאטיה | MBA פסיכולוגיה ארגונית' : 'Satya Method Coach | MBA Organizational Psychology'}
          </span>
        </div>

        {/* Butterfly decoration */}
        <div className="mb-8 animate-breathe">
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            className="mx-auto butterfly-icon"
            style={{ color: 'var(--sand-500)' }}
          >
            <path 
              d="M12 2C12 2 9 5 9 8C9 11 12 14 12 14C12 14 15 11 15 8C15 5 12 2 12 2Z" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M12 14C12 14 6 12 4 15C2 18 4 22 4 22C4 22 8 20 10 17C11.5 14.5 12 14 12 14Z" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M12 14C12 14 18 12 20 15C22 18 20 22 20 22C20 22 16 20 14 17C12.5 14.5 12 14 12 14Z" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Main heading */}
        <h1 
          className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-6 animate-float-in"
          style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--charcoal)' }}
        >
          {t('hero_title')}
          <br />
          <span style={{ color: 'var(--sage-600)' }}>{t('hero_title_span')}</span>
        </h1>

        {/* Subtitle */}
        <p 
          className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto mb-10 animate-float-in animation-delay-200"
          style={{ lineHeight: '1.8' }}
        >
          {t('hero_subtitle')}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-float-in animation-delay-400">
          <button
            onClick={onBookCall}
            className="btn-primary text-base"
            data-testid="hero-cta-btn"
          >
            {t('hero_cta')}
          </button>
          <button
            onClick={scrollToAbout}
            className="btn-secondary text-base"
            data-testid="hero-learn-more-btn"
          >
            {t('hero_cta_secondary')}
          </button>
        </div>

        {/* Scroll indicator */}
        <div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer"
          onClick={scrollToAbout}
        >
          <ArrowDown size={24} className="text-gray-400" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
